import { CurrentUser } from 'customTypes'
import { DocumentNode } from 'graphql'
import { REFERRED_BY_COACH_ID_KEY } from 'hooks/useReferredByCoach'
import { getVideoCallsFlowCustomizationParams } from 'hooks/useVideoCallsFlowCustomization'
import { LocationPaths, VideoCallsPath } from 'location.types'
import has from 'lodash/has'
import { Feature } from 'utils/feature'
import { isKeggWebviewFlow } from 'utils/keggWebviewFlow'
import { LazyComponentWithPreload, lazyPreload } from 'utils/lazyPreload'
import { safeGetLocalStorageItem } from 'utils/localStorage'
import { client } from 'utils/unleash'
import { getUserData } from 'utils/userData'
import {
  MobileAppFeature,
  ClientConfigDocument,
  InsurancePlansDocument,
  UiFlags,
  UserReferralPartner,
  EhrAppointmentStateKind,
  LatestUserEhrAppointmentQuery
} from 'types'
import { getFlowPartnerFromStorage } from './hooks/partnerFlow'
import { isBookingAllowed, getIsFreeCallTrialFlow, canScheduleWithoutPaymentMethod } from './utils'
import { isDexcomVideoCallPartner } from './utils/dexcomPartner'
import { getValidEmailFromSearchParams } from './utils/searchParams'
import { getVideoCallData, ineligiblePlanSelected } from './utils/videoCallsStorage'

export type VideoCallsUiFlags = Pick<UiFlags, 'insuranceInformationSubmitted'>

export interface VideoCallStep {
  path: LocationPaths | VideoCallsPath
  location: string
  featureFlag?: Feature
  completedResolver: (
    user: CurrentUser | undefined,
    latestAppointment: LatestUserEhrAppointmentQuery['latestUserEhrAppointment'],
    uiFlags?: VideoCallsUiFlags | null
  ) => boolean
  canNavigateBack: boolean
  routeComponent: LazyComponentWithPreload<(props: any) => JSX.Element | null>
  noPageAnimation?: boolean
  hideProgressBar?: boolean
  hideSecurityNote?: boolean
  revisitable?: boolean
  hideLogo?: boolean
  requiresStripe?: boolean
  preloadQueries?: DocumentNode[]
  props?: { [key: string]: any }
}

const EntrypointScreen = lazyPreload(() =>
  import('screens/VideoCalls/Entrypoint').then((module) => ({
    default: module.Entrypoint
  }))
)

const ChooseHealthGoalScreen = lazyPreload(() =>
  import('screens/VideoCalls/ChooseHealthGoal').then((module) => ({
    default: module.ChooseHealthGoal
  }))
)

const InsurancePrepScreen = lazyPreload(() =>
  import('screens/VideoCalls/InsurancePrep').then((module) => ({
    default: module.InsurancePrep
  }))
)

const ChooseInsurancePlanScreen = lazyPreload(() =>
  import('screens/VideoCalls/ChooseInsurancePlan').then((module) => ({
    default: module.ChooseInsurancePlan
  }))
)

const SubmitEmailAddressScreen = lazyPreload(() =>
  import('screens/VideoCalls/SubmitEmailAddress').then((module) => ({
    default: module.SubmitEmailAddress
  }))
)

const AddressFormScreen = lazyPreload(() =>
  import('screens/VideoCalls/AddressForm').then((module) => ({
    default: module.AddressForm
  }))
)

const VideoCallsSignUpScreen = lazyPreload(() =>
  import('screens/VideoCalls/SignUp').then((module) => ({
    default: module.SignUp
  }))
)

const FreeVideoCallScreen = lazyPreload(() =>
  import('screens/FreeVideoCall').then((module) => ({
    default: module.FreeVideoCall
  }))
)

const MfpCreditIntroScreen = lazyPreload(() =>
  import('screens/VideoCalls/MfpCreditIntro').then((module) => ({
    default: module.MfpCreditIntro
  }))
)

const SubmitState = lazyPreload(() =>
  import('screens/VideoCalls/SubmitState').then((module) => ({
    default: module.SubmitState
  }))
)

const InsuranceQuestionnaireScreen = lazyPreload(() =>
  import('screens/InsuranceQuestionnaire').then((module) => ({
    default: module.InsuranceQuestionnaireScreen
  }))
)

const ScheduleCallScreen = lazyPreload(() =>
  import('./ScheduleCallStep').then((module) => ({
    default: module.ScheduleCallStep
  }))
)

const UpsellLoaderScreen = lazyPreload(() =>
  import('screens/VideoCalls/UpsellLoader').then((module) => ({
    default: module.UpsellLoader
  }))
)

const VideoCallsOnboardingConfirmationScreen = lazyPreload(() =>
  import('screens/VideoCalls/OnboardingConfirmation').then((module) => ({
    default: module.VideoCallsOnboardingConfirmation
  }))
)

const LabPanelsUpsellScreen = lazyPreload(() =>
  import('screens/VideoCalls/LabPanelsUpsell').then((module) => ({
    default: module.LabPanelsUpsell
  }))
)

const MicrobiomeUpsellScreen = lazyPreload(() =>
  import('screens/VideoCalls/MicrobiomeUpsell').then((module) => ({
    default: module.MicrobiomeUpsell
  }))
)

const CheckoutScreen = lazyPreload(() =>
  import('screens/VideoCalls/Checkout').then((module) => ({
    default: module.Checkout
  }))
)

const CompletedScreen = lazyPreload(() =>
  import('screens/VideoCalls/FlowCompleted').then((module) => ({
    default: module.FlowCompleted
  }))
)

const IntegrateDexcomAccountScreen = lazyPreload(() =>
  import('screens/VideoCalls/IntegrateDexcomAccount').then((module) => ({
    default: module.IntegrateDexcomAccount
  }))
)

const BenefitsCheckScreen = lazyPreload(() =>
  import('screens/VideoCalls/BenefitsCheck').then((module) => ({
    default: module.BenefitsCheck
  }))
)

const AddPaymentMethodScreen = lazyPreload(() =>
  import('screens/VideoCalls/AddPaymentMethod').then((module) => ({
    default: module.AddPaymentMethod
  }))
)

const AddAppointmentAgendaScreen = lazyPreload(() =>
  import('screens/VideoCalls/AddAppointmentAgenda').then((module) => ({
    default: module.AddAppointmentAgenda
  }))
)

const VerifyCoverageScreen = lazyPreload(() =>
  import('screens/VideoCalls/VerifyCoverage').then((module) => ({
    default: module.VerifyCoverage
  }))
)

const HeardAboutNutrisenseScreen = lazyPreload(() =>
  import('screens/VideoCalls/HeardAboutNutrisense').then((module) => ({
    default: module.HeardAboutNutrisense
  }))
)

const AppointmentConfirmedScreen = lazyPreload(() =>
  import('screens/VideoCalls/AppointmentConfirmed/AppointmentConfirmedScreen').then((module) => ({
    default: module.AppointmentConfirmedScreen
  }))
)

const KeggCallConfirmedScreen = lazyPreload(() =>
  import('screens/VideoCalls/Kegg/CallConfirmed').then((module) => ({
    default: module.KeggCallConfirmed
  }))
)

const KeggPickInsuranceProvider = lazyPreload(() =>
  import('screens/VideoCalls/Kegg/PickInsuranceProvider').then((module) => ({
    default: module.KeggPickInsuranceProvider
  }))
)

let initialVideoCallData: ReturnType<typeof getVideoCallData> = null
let initialUserData: ReturnType<typeof getUserData> = null

export const getVideoCallFlow: () => VideoCallStep[] = () => {
  const referredCoachId = safeGetLocalStorageItem(REFERRED_BY_COACH_ID_KEY)
  const partner = getFlowPartnerFromStorage()
  const dexcomFlowSchedulerAtBeginningEnabled = client.isEnabled(Feature.DexcomSchedulerAtBeginning)
  const { forceKeggFlow } = getVideoCallsFlowCustomizationParams()

  const isInfluencerFlow = partner === UserReferralPartner.Influencer
  const isMfpCreditFlow = partner === UserReferralPartner.MfpCredit
  const isDexcomFlow = isDexcomVideoCallPartner(partner)
  const isReferralFlow = Boolean(referredCoachId)
  const isFreeCallTrialFlow = getIsFreeCallTrialFlow()

  const result: VideoCallStep[] = [
    {
      path: LocationPaths.VideoCalls,
      location: LocationPaths.VideoCalls,
      completedResolver: () => true,
      canNavigateBack: false,
      hideProgressBar: true,
      routeComponent: EntrypointScreen
    }
  ]

  const choosePrimaryHealthGoalStep: VideoCallStep = {
    path: VideoCallsPath.ChoosePrimaryHealthGoal,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.ChoosePrimaryHealthGoal}`,
    completedResolver: (user) => {
      if (user?.features.includes(MobileAppFeature.InsuranceCoveredVideoCalls)) {
        return true
      }
      const videoCallData = getVideoCallData()
      return !!videoCallData?.health_goal
    },
    canNavigateBack: isReferralFlow,
    routeComponent: ChooseHealthGoalScreen
  }

  const insurancePrepStep: VideoCallStep = {
    path: VideoCallsPath.InsurancePrep,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.InsurancePrep}`,
    completedResolver: () => {
      const videoCallData = getVideoCallData()
      return Boolean(
        videoCallData?.insurance_prep_seen ||
          videoCallData?.selected_insurance_plan ||
          videoCallData?.insurance_skipped
      )
    },
    canNavigateBack: true,
    revisitable: true,
    routeComponent: InsurancePrepScreen
  }

  const emailAddressStep: VideoCallStep = {
    path: VideoCallsPath.EmailAddress,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.EmailAddress}`,
    completedResolver: (user) => {
      const userData = getUserData()
      const urlEmail = getValidEmailFromSearchParams(new URLSearchParams(window.location.search))

      return !!user?.email || !!userData?.email || !!urlEmail
    },
    canNavigateBack: true,
    routeComponent: SubmitEmailAddressScreen
  }

  const stateStep: VideoCallStep = {
    path: VideoCallsPath.State,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.State}`,
    completedResolver: (user) => {
      const userData = getUserData()
      return !!user?.address?.state || !!userData?.state
    },
    canNavigateBack: true,
    routeComponent: SubmitState
  }

  const signUpStep: VideoCallStep = {
    path: VideoCallsPath.SignUp,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.SignUp}`,
    completedResolver: (user) => !!user,
    canNavigateBack: true,
    revisitable: false,
    routeComponent: VideoCallsSignUpScreen
  }

  const addressFormStep: VideoCallStep = {
    path: VideoCallsPath.AddressForm,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.AddressForm}`,
    completedResolver: (user) => !!user?.address,
    canNavigateBack: false,
    routeComponent: AddressFormScreen,
    requiresStripe: true,
    preloadQueries: [ClientConfigDocument]
  }

  const freeVideoCallStep: VideoCallStep = {
    path: VideoCallsPath.FreeVideoCall,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.FreeVideoCall}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: false,
    routeComponent: FreeVideoCallScreen
  }

  const mfpCreditIntroStep: VideoCallStep = {
    path: VideoCallsPath.MfpCreditIntro,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.MfpCreditIntro}`,
    completedResolver: () => false,
    canNavigateBack: false,
    routeComponent: MfpCreditIntroScreen
  }

  const scheduleCallStep: VideoCallStep = {
    path: VideoCallsPath.ScheduleCall,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.ScheduleCall}`,
    completedResolver: (user, latestAppointment) => {
      const videoCallData = getVideoCallData()

      if (isBookingAllowed({ user })) {
        // User is scheduling/rescheduling via the dashboard
        if (videoCallData?.coming_from_dashboard) {
          return false
        }

        const isAppointmentNotSchedulingPending =
          latestAppointment?.lastState?.kind !== EhrAppointmentStateKind.PendingSchedule

        return !!latestAppointment && isAppointmentNotSchedulingPending
      } else {
        return !!videoCallData?.reserved_appointment
      }
    },
    canNavigateBack: !isReferralFlow,
    routeComponent: ScheduleCallScreen
  }

  const addAppointmentAgendaStep: VideoCallStep = {
    path: VideoCallsPath.AddAppointmentAgenda,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.AddAppointmentAgenda}`,
    completedResolver: (user, latestAppointment) => {
      const isAppointmentNotSchedulingPending =
        latestAppointment?.lastState?.kind !== EhrAppointmentStateKind.PendingSchedule

      if (!!latestAppointment && isAppointmentNotSchedulingPending) {
        return true
      }
      const videoCallData = getVideoCallData()
      return has(videoCallData, 'appointment_agenda')
    },
    canNavigateBack: true,
    routeComponent: AddAppointmentAgendaScreen
  }

  const verifyCoverageStep: VideoCallStep = {
    path: VideoCallsPath.VerifyCoverage,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.VerifyCoverage}`,
    completedResolver: (user) => {
      if (user?.features.includes(MobileAppFeature.InsuranceCoveredVideoCalls)) {
        return true
      }

      if (partner === UserReferralPartner.Influencer) {
        return true
      }

      return false
    },
    canNavigateBack: true,
    routeComponent: VerifyCoverageScreen
  }

  const upsellLoaderStep: VideoCallStep = {
    path: VideoCallsPath.UpsellLoader,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.UpsellLoader}`,
    completedResolver: () => false,
    canNavigateBack: false,
    hideSecurityNote: true,
    routeComponent: UpsellLoaderScreen
  }

  const labPanelsUpsellStep: VideoCallStep = {
    path: VideoCallsPath.LabPanels,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.LabPanels}`,
    featureFlag: Feature.LabPanelsUpsell,
    completedResolver: () => {
      const videoCallData = getVideoCallData()
      return !!videoCallData?.lab_panels_upsell_visited
    },
    canNavigateBack: true,
    routeComponent: LabPanelsUpsellScreen
  }

  const microbiomeUpsellStep: VideoCallStep = {
    path: VideoCallsPath.Microbiome,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.Microbiome}`,
    featureFlag: Feature.MicrobiomeUpsell,
    completedResolver: () => {
      const videoCallData = getVideoCallData()
      return !!videoCallData?.microbiome_upsell_visited
    },
    canNavigateBack: true,
    routeComponent: MicrobiomeUpsellScreen
  }

  const labPanelsCheckoutStep: VideoCallStep = {
    path: VideoCallsPath.Checkout,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.Checkout}`,
    featureFlag: Feature.LabPanelsUpsell,
    completedResolver: () => {
      const videoCallData = getVideoCallData()
      return !!videoCallData?.lab_panels_upsell_visited
    },
    canNavigateBack: true,
    routeComponent: CheckoutScreen
  }

  const integrateDexcomAccountStep: VideoCallStep = {
    path: VideoCallsPath.IntegrateDexcomAccount,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.IntegrateDexcomAccount}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: true,
    routeComponent: IntegrateDexcomAccountScreen
  }

  const completedStep: VideoCallStep = {
    path: VideoCallsPath.Completed,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.Completed}`,
    completedResolver: (user) => {
      return !!user?.dexcomSteloConnection
    },
    canNavigateBack: false,
    routeComponent: CompletedScreen
  }

  const benefitsCheckStep: VideoCallStep = {
    path: VideoCallsPath.BenefitsCheck,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.BenefitsCheck}`,
    completedResolver: (user, _latestAppointment, uiFlags) => {
      if (!user?.dateOfBirth) {
        return true
      }

      if (user.features.includes(MobileAppFeature.InsuranceCoveredVideoCalls)) {
        return true
      }

      if (getVideoCallData()?.insurance_skipped) {
        return true
      }

      return uiFlags?.insuranceInformationSubmitted ?? false
    },
    canNavigateBack: false,
    revisitable: false,
    requiresStripe: true,
    routeComponent: BenefitsCheckScreen
  }

  const chooseInsurancePlanStep: VideoCallStep = {
    path: VideoCallsPath.Insurance,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.Insurance}`,
    completedResolver: (user) => {
      const videoCallData = getVideoCallData()

      return (
        user?.features.includes(MobileAppFeature.InsuranceCoveredVideoCalls) ||
        (!!videoCallData?.selected_insurance_plan && !ineligiblePlanSelected())
      )
    },
    canNavigateBack: true,
    routeComponent: ChooseInsurancePlanScreen,
    preloadQueries: [InsurancePlansDocument]
  }

  const insuranceStepSequence = [insurancePrepStep, chooseInsurancePlanStep]

  const insuranceSurveyStep: VideoCallStep = {
    path: VideoCallsPath.InsuranceQuestionnaire,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.InsuranceQuestionnaire}`,
    completedResolver: (user, _latestAppointment, uiFlags) => {
      if (!user) {
        return false
      }
      if (user.features.includes(MobileAppFeature.InsuranceCoveredVideoCalls)) {
        return true
      }
      if (getVideoCallData()?.insurance_skipped) {
        return true
      }
      return uiFlags?.insuranceInformationSubmitted ?? false
    },
    canNavigateBack: false,
    revisitable: false,
    routeComponent: InsuranceQuestionnaireScreen,
    noPageAnimation: true
  }

  const addPaymentMethodStep: VideoCallStep = {
    path: VideoCallsPath.AddPaymentMethod,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.AddPaymentMethod}`,
    completedResolver: (user, latestAppointment) => {
      if (!user) {
        return false
      }
      const reservationStillNeedsBooking = !!getVideoCallData()?.reserved_appointment
      const bookingWithCreditCardCompleted = !!user.paymentMethod?.stripeId && !!latestAppointment
      // No-card members use this step to confirm/book a reserved slot without seeing the card
      // form. Once the reservation is booked and cleared, the step can be skipped.
      return (
        (canScheduleWithoutPaymentMethod(user) && !reservationStillNeedsBooking) ||
        bookingWithCreditCardCompleted
      )
    },
    requiresStripe: true,
    canNavigateBack: false,
    routeComponent: AddPaymentMethodScreen
  }

  const heardAboutNutrisenseStep: VideoCallStep = {
    path: VideoCallsPath.HeardAboutNutrisense,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.HeardAboutNutrisense}`,
    completedResolver: () => {
      const videoCallData = getVideoCallData()
      return !!videoCallData?.heard_about_us
    },
    canNavigateBack: false,
    routeComponent: HeardAboutNutrisenseScreen
  }

  // Keep onboarding confirmation in the video-calls flow before upsell steps.
  const onboardingConfirmationStep: VideoCallStep = {
    path: VideoCallsPath.AppointmentConfirmed,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.AppointmentConfirmed}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: false,
    routeComponent: VideoCallsOnboardingConfirmationScreen
  }

  // This one is exclusive to Dexcom Users
  // since we need to control via feature flags the upsell steps they will see
  // Its easier to have a separated screen for it
  const appointmentConfirmedStep: VideoCallStep = {
    path: VideoCallsPath.AppointmentConfirmed,
    location: `${LocationPaths.VideoCalls}/${VideoCallsPath.AppointmentConfirmed}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: false,
    routeComponent: AppointmentConfirmedScreen
  }

  if (isInfluencerFlow) {
    result.push(
      emailAddressStep,
      stateStep,
      signUpStep,
      freeVideoCallStep,
      scheduleCallStep,
      addAppointmentAgendaStep,
      addressFormStep,
      completedStep
    )
    return result
  }

  if (isMfpCreditFlow) {
    result.push(
      mfpCreditIntroStep,
      scheduleCallStep,
      addAppointmentAgendaStep,
      emailAddressStep,
      signUpStep,
      addressFormStep,
      addPaymentMethodStep,
      onboardingConfirmationStep
    )
    return result
  }

  if (isFreeCallTrialFlow) {
    result.push(
      emailAddressStep,
      stateStep,
      scheduleCallStep,
      addAppointmentAgendaStep,
      signUpStep,
      verifyCoverageStep,
      benefitsCheckStep,
      insuranceSurveyStep,
      addressFormStep,
      addPaymentMethodStep,
      completedStep
    )
    return result
  }

  if (referredCoachId) {
    result.push(
      scheduleCallStep,
      choosePrimaryHealthGoalStep,
      ...insuranceStepSequence,
      emailAddressStep,
      stateStep,
      addAppointmentAgendaStep,
      signUpStep,
      verifyCoverageStep,
      benefitsCheckStep,
      insuranceSurveyStep,
      addressFormStep,
      addPaymentMethodStep,
      heardAboutNutrisenseStep
    )

    result.push(onboardingConfirmationStep)
    result.push(upsellLoaderStep, labPanelsUpsellStep, microbiomeUpsellStep, labPanelsCheckoutStep)
    result.push(completedStep)

    return result
  }

  if (isKeggWebviewFlow(partner, forceKeggFlow)) {
    // keep initial data to show only the relevant steps
    if (!initialVideoCallData) {
      initialVideoCallData = getVideoCallData() || {}
    }
    if (!initialUserData) {
      initialUserData = getUserData() || {}
    }

    const prefilledEmail =
      initialUserData.email ??
      getValidEmailFromSearchParams(new URLSearchParams(window.location.search))

    const keggCallConfirmedStep: VideoCallStep = {
      path: VideoCallsPath.KeggCallConfirmed,
      location: `${LocationPaths.VideoCalls}/${VideoCallsPath.KeggCallConfirmed}`,
      completedResolver: () => {
        return false
      },
      hideProgressBar: true,
      hideLogo: true,
      canNavigateBack: false,
      hideSecurityNote: true,
      routeComponent: KeggCallConfirmedScreen
    }

    const keggChooseInsurancePlanStep: VideoCallStep = {
      path: VideoCallsPath.Insurance,
      location: `${LocationPaths.VideoCalls}/${VideoCallsPath.Insurance}`,
      completedResolver: (user) => {
        const videoCallData = getVideoCallData()

        return (
          user?.features.includes(MobileAppFeature.InsuranceCoveredVideoCalls) ||
          (!!videoCallData?.selected_insurance_plan && !ineligiblePlanSelected())
        )
      },
      canNavigateBack: true,
      routeComponent: KeggPickInsuranceProvider,
      preloadQueries: [InsurancePlansDocument]
    }

    if (!initialUserData?.email) {
      result.push(emailAddressStep)
    }

    if (!initialVideoCallData?.health_goal) {
      result.push(choosePrimaryHealthGoalStep)
    }

    result.push(
      {
        ...signUpStep,
        props: {
          showPhoneNumberMarketingConsent: !prefilledEmail,
          showTermsOfUseConsent: !!prefilledEmail,
          skipInsuranceBenefitsCheck: true
        }
      },
      stateStep,
      scheduleCallStep,
      insurancePrepStep,
      keggChooseInsurancePlanStep,
      addAppointmentAgendaStep,
      verifyCoverageStep,
      { ...benefitsCheckStep, canNavigateBack: true },
      { ...insuranceSurveyStep, canNavigateBack: true },
      { ...addressFormStep, canNavigateBack: true },
      { ...addPaymentMethodStep, canNavigateBack: true },
      keggCallConfirmedStep
    )

    return result
  }

  if (isDexcomFlow) {
    // If the feature flag is enabled, we show the scheduler at the beginning of the flow
    // plus some other minor changes to the order of the steps
    if (dexcomFlowSchedulerAtBeginningEnabled) {
      result.push(
        choosePrimaryHealthGoalStep,
        scheduleCallStep,
        addAppointmentAgendaStep,
        emailAddressStep,
        ...insuranceStepSequence,
        stateStep,
        signUpStep,
        verifyCoverageStep,
        benefitsCheckStep,
        insuranceSurveyStep,
        addressFormStep,
        addPaymentMethodStep,
        appointmentConfirmedStep,
        labPanelsUpsellStep,
        microbiomeUpsellStep,
        labPanelsCheckoutStep,
        integrateDexcomAccountStep
      )

      return result
    }

    result.push(
      choosePrimaryHealthGoalStep,
      ...insuranceStepSequence,
      emailAddressStep,
      stateStep,
      scheduleCallStep,
      addAppointmentAgendaStep,
      signUpStep,
      verifyCoverageStep,
      benefitsCheckStep,
      insuranceSurveyStep,
      addressFormStep,
      addPaymentMethodStep,
      appointmentConfirmedStep,
      labPanelsUpsellStep,
      microbiomeUpsellStep,
      labPanelsCheckoutStep,
      integrateDexcomAccountStep
    )

    return result
  }

  result.push(
    choosePrimaryHealthGoalStep,
    ...insuranceStepSequence,
    emailAddressStep,
    stateStep,
    scheduleCallStep,
    addAppointmentAgendaStep,
    signUpStep,
    verifyCoverageStep,
    benefitsCheckStep,
    insuranceSurveyStep,
    addressFormStep,
    addPaymentMethodStep
  )

  if (!partner) {
    result.push(heardAboutNutrisenseStep)
  }

  result.push(onboardingConfirmationStep)
  result.push(
    upsellLoaderStep,
    labPanelsUpsellStep,
    microbiomeUpsellStep,
    labPanelsCheckoutStep,
    completedStep
  )

  return result
}
